/**
 * Componente: Formulario de Contribuyente (alta/edición)
 * Modal con los datos de la Licencia Única Nacional de Conducir.
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatearFecha, enmascararCuil, posicionCursorCuil, soloDigitosCuil } from '../utils/fecha';
import CapturaFotoCamara from './CapturaFotoCamara';
import RecorteFotoModal from './RecorteFotoModal';
import ConfirmarDescartarCambiosModal from './ConfirmarDescartarCambiosModal';
import {
  TIPOS_DOCUMENTO,
  filtrarNumeroDocumento,
  errorNumeroDocumento,
  placeholderDocumento,
} from '../utils/documento';
import { STROKE_DEGRADE, IconCerrar, IconAdvertencia, IconCamara, IconSubirArchivo } from './icons';
import { useClasesMunicipio, tituloClase } from '../utils/clasesLicencia';

const API_URL = process.env.REACT_APP_API_URL;

const GRUPOS_SANGUINEOS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const TIPOS_TRAMITE = [
  { value: 'nueva', label: 'Nueva' },
  { value: 'renovacion', label: 'Renovación' },
  { value: 'duplicado', label: 'Duplicado' },
  { value: 'ampliacion', label: 'Ampliación' },
];

const FORM_VACIO = {
  apellido: '',
  nombre: '',
  tipo_documento: 'DNI',
  numero_documento: '',
  cuit_cuil: '',
  fecha_nacimiento: '',
  nacionalidad: 'Argentina',
  sexo: '',
  domicilio_calle: '',
  domicilio_numero: '',
  domicilio_localidad: '',
  domicilio_provincia: '',
  domicilio_cp: '',
  telefono: '',
  email: '',
  grupo_sanguineo: '',
  donante_organos: false,
  usa_lentes: false,
  observaciones_medicas: '',
  clases: [],
  tipo_tramite: 'nueva',
  fecha_emision: '',
  numero_licencia: '',
  numero_tramite: '',
  foto_url: '',
  firma_url: '',
};

const PadronFormModal = ({ contribuyenteId, onClose, onSaved, modoSolicitud = false, modoAdminDirecto = false }) => {
  const [form, setForm] = useState(FORM_VACIO);
  const [cargando, setCargando] = useState(!!contribuyenteId);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  // Para avisar antes de cerrar si hay datos sin guardar: se compara el
  // formulario actual contra una foto del estado inicial (el registro
  // recién cargado, o el formulario vacío en el alta).
  const formInicialRef = useRef(JSON.stringify(FORM_VACIO));
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  // El mensaje del DNI aparece al salir del campo o al intentar guardar, no
  // mientras la persona todavía está tipeando.
  const [documentoTocado, setDocumentoTocado] = useState(false);

  // Método de carga de foto configurado por el admin del municipio:
  // 'camara' | 'archivo' | null (todavía sin configurar → se ofrecen las
  // dos opciones como fallback seguro). metodoElegido es la elección del
  // operador DENTRO de esta sesión de formulario, solo relevante cuando
  // metodoFoto es null.
  const [metodoFoto, setMetodoFoto] = useState(null);
  const [metodoElegido, setMetodoElegido] = useState(null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [errorFoto, setErrorFoto] = useState('');
  // dataURL de la foto elegida por archivo, en lo que se muestra el editor
  // de recorte — null cuando no hay ningún recorte en curso. Distinto del
  // paso de recorte de la cámara, que vive adentro de CapturaFotoCamara.jsx
  // (ahí la imagen ya está en memoria como dataURL desde la captura).
  const [imagenParaRecortar, setImagenParaRecortar] = useState(null);

  // Lista de clases del municipio (GET /api/clases?todas=1): las activas se pueden elegir; las inactivas solo
  // se muestran si la persona ya las tiene. Si el pedido falla o viene vacío, la lista fija de 19 de siempre.
  const { catalogo, cargando: cargandoClases } = useClasesMunicipio();
  // Clases que la persona ya tenía guardadas al abrir (para rotular las que hoy están inactivas).
  const [clasesGuardadas, setClasesGuardadas] = useState([]);

  useEffect(() => {
    const cargarMetodoFoto = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/municipios/configuracion`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMetodoFoto(data.metodo_captura_foto);
      } catch (err) {
        // Si falla la carga del método, mejor no bloquear el formulario:
        // se queda en null (ambas opciones disponibles), el mismo fallback
        // seguro que ya usamos cuando el admin no configuró nada todavía.
        console.error('Error al cargar el método de carga de foto:', err);
      }
    };
    cargarMetodoFoto();
  }, []);

  useEffect(() => {
    if (!contribuyenteId) return;

    const cargarContribuyente = async () => {
      try {
        setCargando(true);
        const token = sessionStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/contribuyentes/${contribuyenteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const c = data.contribuyente;
        setClasesGuardadas(Array.isArray(c.clases) ? c.clases : []);
        const datosCargados = {
          ...FORM_VACIO,
          ...c,
          // Por si el registro es viejo y quedó guardado con guiones (antes
          // de esta mejora, el backend no lo limpiaba) — el estado siempre
          // tiene que quedar en dígitos puros, nunca con el formato visual.
          cuit_cuil: soloDigitosCuil(c.cuit_cuil),
          clases: c.clases || [],
          donante_organos: !!c.donante_organos,
          usa_lentes: !!c.usa_lentes,
          fecha_nacimiento: c.fecha_nacimiento || '',
          fecha_emision: c.fecha_emision || '',
        };
        formInicialRef.current = JSON.stringify(datosCargados);
        setForm(datosCargados);
      } catch (err) {
        console.error('Error al cargar contribuyente:', err);
        setError(err.response?.data?.mensaje || 'Error al cargar el contribuyente');
      } finally {
        setCargando(false);
      }
    };

    cargarContribuyente();
  }, [contribuyenteId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Número de documento: lo que se deja escribir o pegar depende del tipo
  // (DNI/LC/LE solo dígitos hasta 9; Pasaporte letras y números en mayúsculas
  // hasta 20) — reglas en utils/documento.js, las mismas del servidor.
  // (Sin maxLength en el input a propósito: el navegador recortaría el texto
  // pegado ANTES de limpiarlo y se perdería un carácter.)
  const handleChangeDocumento = (e) => {
    const valor = filtrarNumeroDocumento(form.tipo_documento, e.target.value);
    setForm((prev) => ({ ...prev, numero_documento: valor }));
  };

  // Al cambiar el tipo NO se borra ni se recorta lo ya escrito: se revalida
  // el mismo número con la regla del tipo nuevo y, si no cumple, el error
  // aparece enseguida para que la persona lo corrija.
  const handleChangeTipoDocumento = (e) => {
    const tipo = e.target.value;
    setForm((prev) => ({
      ...prev,
      tipo_documento: tipo,
      numero_documento: tipo === 'Pasaporte' ? prev.numero_documento.toUpperCase() : prev.numero_documento,
    }));
    if (form.numero_documento) setDocumentoTocado(true);
  };

  const errorDocumento = errorNumeroDocumento(form.tipo_documento, form.numero_documento);
  const documentoInvalido = !!errorDocumento;
  // En edición, un registro viejo con el documento mal cargado muestra el
  // aviso desde el principio (hay que corregirlo para poder guardar).
  const mostrarErrorDocumento = documentoInvalido && (documentoTocado || !!contribuyenteId);
  const esPasaporte = form.tipo_documento === 'Pasaporte';

  const hayCambiosSinGuardar = JSON.stringify(form) !== formInicialRef.current;

  // X y Cancelar: el fondo oscuro ya no cierra la ventana. Con datos
  // modificados se pide confirmación antes de descartarlos.
  const pedirCerrar = () => {
    if (guardando) return;
    if (hayCambiosSinGuardar) {
      setConfirmarDescarte(true);
    } else {
      onClose();
    }
  };

  // Máscara en vivo del CUIL: en el input se ve XX-XXXXXXXX-X (funciona
  // igual al tipear que al pegar un valor ya formateado o crudo, con el
  // cursor preservado), pero en el estado — y por lo tanto en lo que se
  // manda al backend — solo quedan los 11 dígitos, sin guiones. El input
  // muestra enmascararCuil(form.cuit_cuil) más abajo; acá solo se guardan
  // los dígitos limpios.
  const handleChangeCuil = (e) => {
    const input = e.target;
    const cursorPrevio = input.selectionStart;
    const digitos = soloDigitosCuil(input.value);
    const formateado = enmascararCuil(digitos);
    const nuevaPosicion = posicionCursorCuil(input.value, cursorPrevio, formateado);
    setForm((prev) => ({ ...prev, cuit_cuil: digitos }));
    requestAnimationFrame(() => input.setSelectionRange(nuevaPosicion, nuevaPosicion));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  // Recibe un File (input de archivo) o un Blob (captura de cámara) — a
  // FormData le da lo mismo, ambos se mandan igual.
  const subirFoto = async (fileOBlob) => {
    try {
      setSubiendoFoto(true);
      setErrorFoto('');
      const token = sessionStorage.getItem('token');
      const formData = new FormData();
      formData.append('foto', fileOBlob, 'foto.jpg');
      const { data } = await axios.post(`${API_URL}/contribuyentes/foto`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm((prev) => ({ ...prev, foto_url: data.url }));
    } catch (err) {
      console.error('Error al subir la foto:', err);
      setErrorFoto(err.response?.data?.mensaje || 'Error al subir la foto');
    } finally {
      setSubiendoFoto(false);
    }
  };

  // Ya no sube el archivo directo: lo lee como dataURL y abre el editor de
  // recorte (RecorteFotoModal) — recién al confirmar ahí se genera el JPEG
  // recortado y se sube, igual que siempre.
  const alElegirArchivoFoto = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const lector = new FileReader();
    lector.onload = () => setImagenParaRecortar(lector.result);
    lector.readAsDataURL(file);
  };

  const alConfirmarRecorteArchivo = (blob) => {
    cerrarEditorFoto();
    subirFoto(blob);
  };

  // "Editar foto": reabre el mismo editor de recorte sobre la foto ya
  // guardada. Se baja la imagen como blob (mismo origen, /api/uploads) y se
  // le pasa al editor un object URL: así el canvas nunca queda "tainted" ni
  // hace falta tocar CORS. Si no se puede bajar, se avisa y la foto queda
  // como estaba.
  const [abriendoEditorFoto, setAbriendoEditorFoto] = useState(false);
  const editarFotoGuardada = async () => {
    try {
      setAbriendoEditorFoto(true);
      setErrorFoto('');
      const respuesta = await fetch(form.foto_url, { credentials: 'same-origin' });
      if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
      const blob = await respuesta.blob();
      if (!blob.type.startsWith('image/')) throw new Error('No es una imagen');
      setImagenParaRecortar(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Error al cargar la foto guardada para editarla:', err);
      setErrorFoto('No se pudo cargar la foto guardada para editarla. Probá de nuevo, o quitá la foto y subí una nueva.');
    } finally {
      setAbriendoEditorFoto(false);
    }
  };

  const cerrarEditorFoto = () => {
    setImagenParaRecortar((actual) => {
      if (actual && actual.startsWith('blob:')) URL.revokeObjectURL(actual);
      return null;
    });
  };

  const quitarFoto = () => {
    setForm((prev) => ({ ...prev, foto_url: '' }));
    setMetodoElegido(null);
    setErrorFoto('');
  };

  // Códigos que se pueden elegir (activos en el municipio) y, aparte, los que la persona ya tiene y hoy están
  // inactivos o fuera de la lista: se muestran marcados como "inactiva", se pueden quitar pero no volver a agregar.
  const codigosActivos = new Set(catalogo.activas.map((c) => c.codigo));
  const clasesInactivasDeLaPersona = clasesGuardadas.filter((c) => !codigosActivos.has(c));
  const hayDescripciones = catalogo.activas.some((c) => c.descripcion) || clasesInactivasDeLaPersona.some((c) => catalogo.porCodigo[c]?.descripcion);

  const toggleClase = (clase) => {
    setForm((prev) => {
      if (prev.clases.includes(clase)) return { ...prev, clases: prev.clases.filter((c) => c !== clase) };
      if (!codigosActivos.has(clase)) return prev; // inactiva: no se puede volver a agregar
      return { ...prev, clases: [...prev.clases, clase] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.apellido || !form.nombre || !form.numero_documento || !form.fecha_nacimiento) {
      setError('Completá apellido, nombre, documento y fecha de nacimiento');
      setDocumentoTocado(true);
      return;
    }
    if (errorDocumento) {
      setDocumentoTocado(true);
      setError(errorDocumento);
      return;
    }
    if (form.clases.length === 0) {
      setError('Seleccioná al menos una clase de licencia');
      return;
    }

    try {
      setGuardando(true);
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // fecha_emision/fecha_vencimiento/numero_licencia no se mandan nunca desde
      // este formulario: el backend las ignora igual, pero mejor ni ofrecerlas.
      // Fechas solo cambian con "Renovar"; numero_licencia se deriva del DNI.
      const { fecha_emision, fecha_vencimiento, numero_licencia, ...payload } = form;

      if (contribuyenteId) {
        await axios.put(`${API_URL}/contribuyentes/${contribuyenteId}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/contribuyentes`, payload, { headers });
      }

      // En modo solicitud, el PUT ya guardó los datos revisados — ahora
      // hace falta además disparar el pedido de reactivación. Si esta
      // segunda llamada falla (ej. 409 por solicitud ya pendiente), el
      // modal queda abierto con el error a la vista: los datos del
      // contribuyente ya se guardaron, pero la solicitud no se envió.
      // modoAdminDirecto es el equivalente para un admin resolviendo
      // "Solicitar alta" él mismo: mismo PUT de revisión, pero reactiva
      // directo (reactivar-directo) en vez de mandar un pedido a otro admin.
      if (modoSolicitud) {
        await axios.post(`${API_URL}/contribuyentes/${contribuyenteId}/solicitar-reactivacion`, {}, { headers });
      } else if (modoAdminDirecto) {
        await axios.post(`${API_URL}/contribuyentes/${contribuyenteId}/reactivar-directo`, {}, { headers });
      }

      onSaved();
    } catch (err) {
      console.error('Error al guardar contribuyente:', err);
      setError(err.response?.data?.mensaje || 'Error al guardar el contribuyente');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{modoAdminDirecto ? 'Revisar y reactivar' : modoSolicitud ? 'Revisar datos y solicitar alta' : contribuyenteId ? 'Editar contribuyente' : 'Nuevo contribuyente'}</h2>
          <button type="button" className="modal-close" onClick={pedirCerrar}><IconCerrar size={18} /></button>
        </div>

        {cargando ? (
          <div className="padron-loading">
            <div className="loading-spinner"></div>
            <p>Cargando datos...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="modal-body">
              {modoSolicitud && (
                <div className="form-hint">
                  Esta licencia venció fuera del período de gracia. Revisá los datos antes de solicitar la reactivación.
                </div>
              )}

              {modoAdminDirecto && (
                <div className="form-hint">
                  Esta licencia venció fuera del período de gracia. Revisá los datos antes de reactivarla — al confirmar, se reactiva directo, sin pasar por otro admin.
                </div>
              )}

              {error && (
                <div className="alert alert-error">
                  <IconAdvertencia size={18} />
                  <p>{error}</p>
                </div>
              )}

              {/* DATOS PERSONALES */}
              <div className="form-section">
                <h3>Datos personales</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Apellido *</label>
                    <input className="padron-input padron-input-mayus" name="apellido" value={form.apellido} onChange={handleChange} required />
                  </div>
                  <div className="form-field">
                    <label>Nombre *</label>
                    <input className="padron-input padron-input-mayus" name="nombre" value={form.nombre} onChange={handleChange} required />
                  </div>
                  <div className="form-field">
                    <label>Tipo de documento</label>
                    <select className="padron-input" name="tipo_documento" value={form.tipo_documento} onChange={handleChangeTipoDocumento}>
                      {TIPOS_DOCUMENTO.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Número de documento *</label>
                    <input
                      className={`padron-input${mostrarErrorDocumento ? ' padron-input-invalido' : ''}`}
                      name="numero_documento"
                      value={form.numero_documento}
                      onChange={handleChangeDocumento}
                      onBlur={() => setDocumentoTocado(true)}
                      inputMode={esPasaporte ? 'text' : 'numeric'}
                      autoComplete="off"
                      autoCapitalize={esPasaporte ? 'characters' : 'off'}
                      spellCheck={false}
                      placeholder={placeholderDocumento(form.tipo_documento)}
                      aria-invalid={mostrarErrorDocumento}
                      required
                    />
                    {mostrarErrorDocumento && (
                      <span className="form-error-campo" role="alert">{errorDocumento}</span>
                    )}
                  </div>
                  <div className="form-field">
                    <label>CUIT/CUIL</label>
                    <input className="padron-input" name="cuit_cuil" value={enmascararCuil(form.cuit_cuil)} onChange={handleChangeCuil} placeholder="20-12345678-9" inputMode="numeric" />
                  </div>
                  <div className="form-field">
                    <label>Fecha de nacimiento *</label>
                    <input className="padron-input" type="date" name="fecha_nacimiento" value={form.fecha_nacimiento} onChange={handleChange} required />
                  </div>
                  <div className="form-field">
                    <label>Nacionalidad</label>
                    <input className="padron-input padron-input-mayus" name="nacionalidad" value={form.nacionalidad} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Sexo</label>
                    <select className="padron-input" name="sexo" value={form.sexo} onChange={handleChange}>
                      <option value="">Seleccionar...</option>
                      <option value="M">Masculino</option>
                      <option value="F">Femenino</option>
                      <option value="X">X</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Calle</label>
                    <input className="padron-input padron-input-mayus" name="domicilio_calle" value={form.domicilio_calle} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Número</label>
                    <input className="padron-input" name="domicilio_numero" value={form.domicilio_numero} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Localidad</label>
                    <input className="padron-input padron-input-mayus" name="domicilio_localidad" value={form.domicilio_localidad} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Provincia</label>
                    <input className="padron-input padron-input-mayus" name="domicilio_provincia" value={form.domicilio_provincia} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Código postal</label>
                    <input className="padron-input" name="domicilio_cp" value={form.domicilio_cp} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input className="padron-input" name="telefono" value={form.telefono} onChange={handleChange} />
                  </div>
                  <div className="form-field">
                    <label>Email</label>
                    <input className="padron-input" type="email" name="email" value={form.email} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* DATOS MÉDICOS */}
              <div className="form-section">
                <h3>Datos médicos</h3>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Grupo sanguíneo</label>
                    <select className="padron-input" name="grupo_sanguineo" value={form.grupo_sanguineo} onChange={handleChange}>
                      <option value="">Seleccionar...</option>
                      {GRUPOS_SANGUINEOS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="form-field form-field-checkbox">
                    <label>
                      <input type="checkbox" name="donante_organos" checked={form.donante_organos} onChange={handleCheckbox} />
                      Donante de órganos
                    </label>
                  </div>
                  <div className="form-field form-field-checkbox">
                    <label>
                      <input type="checkbox" name="usa_lentes" checked={form.usa_lentes} onChange={handleCheckbox} />
                      Usa lentes
                    </label>
                  </div>
                  <div className="form-field form-field-full">
                    <label>Observaciones médicas</label>
                    <textarea className="padron-input padron-input-mayus" name="observaciones_medicas" value={form.observaciones_medicas} onChange={handleChange} rows={3} />
                  </div>
                </div>
              </div>

              {/* DATOS DE LICENCIA */}
              <div className="form-section">
                <h3>Datos de licencia</h3>
                <div className="form-field form-field-full">
                  <label>Clases *</label>
                  {cargandoClases && <span className="form-hint">Cargando las clases del municipio...</span>}
                  <div className={`clases-grid ${hayDescripciones ? 'clases-grid--descripciones' : ''}`} hidden={cargandoClases}>
                    {catalogo.activas.map((c) => (
                      <label
                        key={c.codigo}
                        className={`clase-checkbox ${form.clases.includes(c.codigo) ? 'checked' : ''}`}
                        title={tituloClase(catalogo, c.codigo)}
                      >
                        <input
                          type="checkbox"
                          checked={form.clases.includes(c.codigo)}
                          onChange={() => toggleClase(c.codigo)}
                        />
                        <span className="clase-texto">
                          <span className="clase-codigo">{c.codigo}</span>
                          {c.descripcion && <span className="clase-descripcion">{c.descripcion}</span>}
                        </span>
                      </label>
                    ))}
                    {clasesInactivasDeLaPersona.map((codigo) => {
                      const marcada = form.clases.includes(codigo);
                      const descripcion = catalogo.porCodigo[codigo]?.descripcion;
                      return (
                        <label
                          key={`inactiva-${codigo}`}
                          className={`clase-checkbox clase-checkbox--inactiva ${marcada ? 'checked' : 'clase-checkbox--deshabilitada'}`}
                          title={`${tituloClase(catalogo, codigo)} — clase inactiva: ${marcada ? 'se puede quitar' : 'no se puede volver a agregar'}`}
                        >
                          <input
                            type="checkbox"
                            checked={marcada}
                            disabled={!marcada}
                            onChange={() => toggleClase(codigo)}
                          />
                          <span className="clase-texto">
                            <span className="clase-codigo">
                              {codigo} <span className="clase-marca-inactiva">inactiva</span>
                            </span>
                            {descripcion && <span className="clase-descripcion">{descripcion}</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {clasesInactivasDeLaPersona.length > 0 && (
                    <span className="form-hint">
                      Las clases marcadas "inactiva" ya no están habilitadas en este municipio: podés quitarlas, pero no volver a agregarlas.
                    </span>
                  )}
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Tipo de trámite</label>
                    <select className="padron-input" name="tipo_tramite" value={form.tipo_tramite} onChange={handleChange}>
                      {TIPOS_TRAMITE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Fecha de emisión</label>
                    <div className="padron-readonly">
                      {contribuyenteId ? formatearFecha(form.fecha_emision) : 'Se asigna automáticamente: hoy'}
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Fecha de vencimiento</label>
                    <div className="padron-readonly">
                      {contribuyenteId ? formatearFecha(form.fecha_vencimiento) : 'Se calcula automáticamente al guardar'}
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Número de licencia</label>
                    <div className="padron-readonly">
                      {contribuyenteId ? form.numero_licencia : 'Se asigna automáticamente: mismo número que el documento'}
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Número de trámite</label>
                    <input className="padron-input" name="numero_tramite" value={form.numero_tramite} onChange={handleChange} />
                  </div>
                </div>
                <span className="form-hint">
                  La fecha de emisión y el vencimiento solo se pueden actualizar con el botón "Renovar" de la tabla del Padrón (no desde este formulario).
                </span>
              </div>

              {/* MULTIMEDIA */}
              <div className="form-section">
                <h3>Multimedia</h3>

                <div className="form-field form-field-full">
                  <label>Foto del contribuyente</label>

                  {errorFoto && (
                    <div className="alert alert-error">
                      <IconAdvertencia size={18} />
                      <p>{errorFoto}</p>
                    </div>
                  )}

                  {form.foto_url ? (
                    <div className="padron-foto-preview">
                      <img src={form.foto_url} alt="Foto del contribuyente" />
                      <div className="padron-foto-acciones">
                        <button
                          type="button"
                          className="btn-accion btn-editar"
                          onClick={editarFotoGuardada}
                          disabled={abriendoEditorFoto || subiendoFoto}
                        >
                          {abriendoEditorFoto ? 'Cargando...' : 'Editar foto'}
                        </button>
                        <button type="button" className="btn-accion btn-editar" onClick={quitarFoto} disabled={subiendoFoto}>
                          Quitar foto
                        </button>
                        {subiendoFoto && (
                          <span className="form-hint"><span className="btn-spinner"></span> Subiendo...</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {metodoFoto === null && !metodoElegido && (
                        <div className="padron-foto-metodo-selector">
                          <button type="button" className="btn-accion btn-editar" onClick={() => setMetodoElegido('camara')}>
                            <IconCamara size={14} stroke={STROKE_DEGRADE} /> Tomar foto con cámara
                          </button>
                          <button type="button" className="btn-accion btn-editar" onClick={() => setMetodoElegido('archivo')}>
                            <IconSubirArchivo size={14} stroke={STROKE_DEGRADE} /> Subir archivo
                          </button>
                        </div>
                      )}

                      {(metodoFoto === 'camara' || metodoElegido === 'camara') && (
                        <CapturaFotoCamara onCapturar={subirFoto} subiendo={subiendoFoto} />
                      )}

                      {(metodoFoto === 'archivo' || metodoElegido === 'archivo') && (
                        <>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="padron-input"
                            onChange={alElegirArchivoFoto}
                            disabled={subiendoFoto}
                          />
                          {subiendoFoto && (
                            <span className="form-hint"><span className="btn-spinner"></span> Subiendo...</span>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                <div className="form-grid">
                  <div className="form-field">
                    <label>URL de firma</label>
                    <input className="padron-input" name="firma_url" value={form.firma_url} onChange={handleChange} placeholder="Carga de archivos próximamente" />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-cancelar" onClick={pedirCerrar} disabled={guardando}>
                Cancelar
              </button>
              <button type="submit" className="btn-guardar" disabled={guardando}>
                {modoAdminDirecto
                  ? (guardando ? 'Reactivando...' : 'Reactivar')
                  : modoSolicitud
                    ? (guardando ? 'Enviando...' : 'Enviar solicitud')
                    : (guardando ? 'Guardando...' : 'Guardar')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
    {confirmarDescarte && (
      <ConfirmarDescartarCambiosModal
        onSeguirEditando={() => setConfirmarDescarte(false)}
        onDescartar={() => { setConfirmarDescarte(false); onClose(); }}
      />
    )}
    {imagenParaRecortar && (
      <RecorteFotoModal
        imagenSrc={imagenParaRecortar}
        subiendo={subiendoFoto}
        onConfirmar={alConfirmarRecorteArchivo}
        onCancelar={cerrarEditorFoto}
      />
    )}
    </>
  );
};

export default PadronFormModal;
